import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import LandingHero from "../components/LandingHero";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const { settings } = useSettings();
  const brand = settings?.primary_color || "#0D9488";

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LandingHero settings={settings}>
      {sent ? (
        <div className="mt-10 space-y-6" data-testid="forgot-password-sent">
          <div
            className="flex items-center justify-center w-14 h-14 border"
            style={{ borderColor: brand }}
          >
            <MailCheck className="w-6 h-6" style={{ color: brand }} />
          </div>
          <div className="space-y-3">
            <h2 className="text-xl text-slate-900 uppercase tracking-widest">Check your email</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              If an account exists for{" "}
              <span className="text-slate-900 font-mono-num">{email}</span>, we&apos;ve sent a
              password reset link. It will expire in <span style={{ color: brand }}>1 hour</span>.
            </p>
            <p className="text-xs text-slate-500 leading-relaxed">
              Didn&apos;t get it? Check your spam folder or{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="underline hover:text-slate-800"
                data-testid="forgot-password-retry"
              >
                try again
              </button>
              .
            </p>
          </div>
          <div className="pt-4 border-t border-slate-200">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
              data-testid="forgot-password-back-to-login"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="forgot-password-form">
          <div className="space-y-2">
            <h2 className="text-xl text-slate-900 uppercase tracking-widest">Forgot password</h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              Enter your account email. We&apos;ll send you a link to reset your password.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono-num">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-50 border-slate-200 text-slate-900 h-12 rounded-none focus-visible:ring-[#0D9488] focus-visible:border-[#0D9488]"
              placeholder="you@example.com"
              data-testid="forgot-password-email"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-white uppercase tracking-widest font-semibold rounded-none transition-colors"
            style={{ backgroundColor: brand }}
            data-testid="forgot-password-submit"
          >
            {submitting ? "Sending…" : "Send reset link"}
          </Button>
          <div className="pt-4 border-t border-slate-200">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
              data-testid="forgot-password-cancel"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        </form>
      )}
    </LandingHero>
  );
}
